(function () {
  const CLIP_COLLAPSED = 'is-collapsed';
  const BLOCK_EXPANDED = 'cap-section-block--body-expanded';

  function initTruncate(root) {
    if (root.dataset.brandAtPerfTruncateInit === '1') {
      return;
    }
    root.dataset.brandAtPerfTruncateInit = '1';

    const clip = root.querySelector('.cap-section-body-clip');
    const body = root.querySelector('.cap-section-body');
    const btn = root.querySelector('.cap-section-read-more');
    const block = root.closest('.cap-section-block');
    if (!clip || !body || !btn || !block) {
      return;
    }

    const readMore = root.getAttribute('data-read-more') || 'Read more';
    const readLess = root.getAttribute('data-read-less') || 'Read less';
    const readMoreLabel = root.getAttribute('data-read-more-label') || readMore;
    const readLessLabel = root.getAttribute('data-read-less-label') || readLess;
    const panel = root.closest('.accordion-panel');

    let resizeTimer = null;
    /** @type {number | null} */
    let raf = null;
    let zeroHeightRetries = 0;
    let hasOverflow = false;

    function scheduleMeasure() {
      if (raf !== null) {
        cancelAnimationFrame(raf);
      }
      raf = requestAnimationFrame(() => {
        raf = requestAnimationFrame(() => {
          raf = null;
          void runMeasureAfterFonts();
        });
      });
    }

    async function runMeasureAfterFonts() {
      try {
        if (document.fonts?.ready) {
          await document.fonts.ready;
        }
      } catch {
        // Still measure if font loading fails
      }
      measure();
    }

    /**
     * @param {boolean} expanded
     */
    function setExpandedUi(expanded) {
      if (expanded) {
        block.classList.add(BLOCK_EXPANDED);
        clip.classList.remove(CLIP_COLLAPSED);
        block.style.height = 'auto';
        block.style.maxHeight = 'none';
        btn.textContent = readLess;
        btn.setAttribute('aria-label', readLessLabel);
      } else {
        block.classList.remove(BLOCK_EXPANDED);
        clip.classList.add(CLIP_COLLAPSED);
        block.style.height = '';
        block.style.maxHeight = '';
        btn.textContent = readMore;
        btn.setAttribute('aria-label', readMoreLabel);
      }

      syncTruncateA11y(root, clip, body, btn, expanded, hasOverflow);
    }

    function measure() {
      if (panel?.hidden) {
        return;
      }

      const userExpanded = block.classList.contains(BLOCK_EXPANDED);

      block.classList.remove(BLOCK_EXPANDED);
      clip.classList.add(CLIP_COLLAPSED);

      requestAnimationFrame(() => {
        if (clip.clientHeight < 1 && body.scrollHeight > 0) {
          clip.classList.remove(CLIP_COLLAPSED);
          if (userExpanded) {
            block.classList.add(BLOCK_EXPANDED);
          }
          if (zeroHeightRetries < 8) {
            zeroHeightRetries += 1;
            setTimeout(scheduleMeasure, 100);
          }
          return;
        }

        zeroHeightRetries = 0;
        hasOverflow = body.scrollHeight > clip.clientHeight + 2;

        if (!hasOverflow) {
          clip.classList.remove(CLIP_COLLAPSED);
          block.classList.remove(BLOCK_EXPANDED);
          block.style.height = '';
          block.style.maxHeight = '';
          btn.hidden = true;
          syncTruncateA11y(root, clip, body, btn, false, false);
          return;
        }

        btn.hidden = false;
        setExpandedUi(userExpanded);
      });
    }

    function onButtonClick() {
      setExpandedUi(!block.classList.contains(BLOCK_EXPANDED));
    }

    function onWindowResize() {
      if (resizeTimer !== null) {
        clearTimeout(resizeTimer);
      }
      resizeTimer = window.setTimeout(() => {
        resizeTimer = null;
        scheduleMeasure();
      }, 150);
    }

    /**
     * Runs after `accessible-accordion` updates `hidden` / aria (toggle uses click or keydown).
     */
    function onAccordionMayHaveToggled(event) {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (!target.closest('.accordion-trigger')) {
        return;
      }
      requestAnimationFrame(() => {
        scheduleMeasure();
      });
    }

    btn.addEventListener('click', onButtonClick);

    for (const img of body.querySelectorAll('img')) {
      if (img.complete) {
        continue;
      }
      img.addEventListener('load', scheduleMeasure);
      img.addEventListener('error', scheduleMeasure);
    }

    const accordion = panel?.closest('accessible-accordion');
    if (accordion) {
      accordion.addEventListener('click', onAccordionMayHaveToggled);
      accordion.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
          return;
        }
        onAccordionMayHaveToggled(event);
      });
      if (!panel.hidden) {
        scheduleMeasure();
      }
    } else {
      scheduleMeasure();
    }

    window.addEventListener('resize', onWindowResize);
    window.addEventListener('orientationchange', onWindowResize);
  }

  function boot() {
    for (const root of document.querySelectorAll('[data-brand-at-perf-truncate]')) {
      if (root instanceof HTMLElement) {
        initTruncate(root);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /**
   * overflow:hidden is visual only — NVDA still reads clipped text in browse mode.
   * When collapsed: hide the full body from AT and expose only the visible lines
   * in a screen-reader excerpt placed before the Read more button.
   */
  function getVisiblePlainText(clip, body) {
    const clipBottom = clip.getBoundingClientRect().bottom;
    const range = document.createRange();
    const parts = [];

    const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        return node.textContent?.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      },
    });

    let node = walker.nextNode();
    while (node) {
      const text = node.textContent || '';
      let visibleChars = '';

      for (let i = 0; i < text.length; i++) {
        range.setStart(node, i);
        range.setEnd(node, i + 1);

        let charVisible = false;
        for (const rect of range.getClientRects()) {
          if (rect.height > 0 && rect.top < clipBottom - 1) {
            charVisible = true;
            break;
          }
        }

        if (charVisible) {
          visibleChars += text[i];
        } else if (visibleChars) {
          break;
        }
      }

      if (visibleChars.trim()) {
        parts.push(visibleChars.trim());
      }

      node = walker.nextNode();
    }

    return parts.join(' ').replace(/\s+/g, ' ').trim();
  }

  function syncTruncateA11y(root, clip, body, btn, expanded, hasOverflow) {
    const excerptId = `${body.id}-sr-excerpt`;
    let excerpt = root.querySelector(`#${excerptId}`);

    const clearSrExcerpt = () => {
      body.removeAttribute('aria-hidden');
      excerpt?.remove();
    };

    if (!hasOverflow) {
      btn.removeAttribute('aria-expanded');
      clearSrExcerpt();
      return;
    }

    btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');

    if (expanded) {
      clearSrExcerpt();
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const text = getVisiblePlainText(clip, body);
        if (!text) {
          return;
        }

        excerpt = root.querySelector(`#${excerptId}`);
        if (!excerpt) {
          excerpt = document.createElement('div');
          excerpt.id = excerptId;
          excerpt.className = 'visually-hidden';
          root.insertBefore(excerpt, btn);
        }

        excerpt.textContent = text;
        body.setAttribute('aria-hidden', 'true');
      });
    });
  }
})();
